import type {
  IntegrationManifest,
  IntegrationManifestService,
  IntegrationManifestsResponse,
  IntegrationUserAgent,
} from '@pigello/pigello-matrix';

export const getAssociatedManifest = (
  agent: IntegrationUserAgent,
  manifests: IntegrationManifestsResponse
) => {
  const manifest = manifests.find((manifest) => {
    const agentServiceId = manifest.uuid_conversions[agent.serviceId];

    if (!agentServiceId) return false;

    return Boolean(
      Object.keys(manifest.services).find((whichService) => {
        if (whichService === agentServiceId) return true;
        return false;
      })
    );
  });

  return manifest;
};

export const getAssociatedService = (
  agent: IntegrationUserAgent,
  manifests: IntegrationManifestsResponse,
  _manifest?: IntegrationManifest
) => {
  const manifest = _manifest ?? getAssociatedManifest(agent, manifests);
  if (!manifest) return undefined;

  const agentServiceVersion = manifest.uuid_conversions[agent.serviceVersionId];

  let service: IntegrationManifestService | null = null;

  loop: for (const whichService of Object.keys(manifest.services)) {
    const serviceVersions = Object.keys(manifest.services[whichService]);

    for (const serviceVersion of serviceVersions) {
      if (serviceVersion === agentServiceVersion) {
        service = manifest.services[whichService][serviceVersion];
        break loop;
      }
    }
  }

  return service;
};

export const parseServiceVersionToNumber = (
  serviceVersion: string | undefined
) => {
  if (!serviceVersion) return 0;
  try {
    const num = parseInt(serviceVersion.replaceAll('.', ''), 10);
    return num;
  } catch {
    return 0;
  }
};

export const getLatestIntegrationService = (
  serviceVersions: Record<string, IntegrationManifestService> | undefined,
  uuidConversions: Record<string, string> | undefined
) => {
  let latest = {
    service: serviceVersions?.[Object.keys(serviceVersions)[0]],
    versionStr: Object.keys(serviceVersions ?? {})[0],
    version: parseServiceVersionToNumber(
      uuidConversions?.[Object.keys(serviceVersions ?? {})[0]]
    ),
  };

  for (const serviceVersion of Object.keys(serviceVersions ?? {})) {
    const version = parseServiceVersionToNumber(
      uuidConversions?.[serviceVersion]
    );
    if (version > latest.version) {
      latest = {
        version,
        versionStr: serviceVersion,
        service: serviceVersions?.[serviceVersion],
      };
    }
  }

  return {
    version: latest.versionStr,
    service: latest.service,
  };
};

const getReversedUUIDConversions = (manifest: IntegrationManifest) => {
  const reversedConversions: Record<string, string> = {};

  for (const [key, value] of Object.entries(manifest.uuid_conversions)) {
    reversedConversions[value] = key;
  }

  return reversedConversions;
};

export const convertStringToManifestId = (
  manifest: IntegrationManifest,
  str: string
): string | null => {
  const conversions = getReversedUUIDConversions(manifest);

  if (!(str in conversions)) return null;

  return conversions[str];
};
